import {
  SessionDto,
  TestDataGenerationResultDto,
} from '../api/client/model/models';

/** SessionStorage key under which the test-data generator persists its result. */
export const DEV_TOOLS_GENERATED_DATA_KEY = 'dev-tools.generatedData';

/** Reads the persisted test-data generation result, or null if absent/invalid. */
export function loadGeneratedTestData(): TestDataGenerationResultDto | null {
  try {
    const raw = sessionStorage.getItem(DEV_TOOLS_GENERATED_DATA_KEY);
    return raw ? (JSON.parse(raw) as TestDataGenerationResultDto) : null;
  } catch {
    return null;
  }
}

/** Reads the persisted, login-ready test-user sessions (empty if none). */
export function loadGeneratedTestSessions(): SessionDto[] {
  return loadGeneratedTestData()?.userSessions ?? [];
}

/** Reads the persisted snapshot for one generated test user, or undefined. */
export function findGeneratedTestSession(
  userId: number
): SessionDto | undefined {
  return loadGeneratedTestSessions().find((s) => s.user?.id === userId);
}

/**
 * Mirrors a freshly written live session into the persisted test-user
 * snapshot, if that user is one of the generated test users.
 *
 * Refresh tokens are single-use and rotate on every /session/refresh and
 * /session/tenant-login. The snapshot is written once at generation time, so
 * without this a later switch back to that user would restore an
 * already-rotated refresh token; its next refresh is then answered with
 * "Refresh token reuse detected" and the backend revokes the whole session.
 *
 * No-op (and never throws) when nothing is stored, e.g. outside dev tools.
 */
export function syncGeneratedTestSession(session: SessionDto): void {
  try {
    const data = loadGeneratedTestData();
    const sessions = data?.userSessions;
    const index = sessions?.findIndex((s) => s.user?.id === session.user?.id);
    if (!data || !sessions || index === undefined || index < 0) {
      return;
    }
    sessions[index] = { ...sessions[index], ...session };
    sessionStorage.setItem(DEV_TOOLS_GENERATED_DATA_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable/quota: the snapshot just stays as it was.
  }
}
